/**
 * BranchContactDialog Component
 *
 * Dialog component for creating and editing branch contact persons.
 * Implements the contact form structure from the implementation plan.
 */

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { getLanguageOptions } from '@/constants/branchContactOptions';
import { useBranchContactForm } from '@/hooks/useBranchContact';
import { useLocalBranches } from '@/hooks/useLocalBranch';
import { useFormsTranslation } from '@/hooks/useTranslation';
import type { BranchContactDialogProps } from '@/types/branchContact.types';
import { Building, Phone, Save, User, X } from 'lucide-react';
import React from 'react';

const BranchContactDialog: React.FC<BranchContactDialogProps> = ({
  open,
  onOpenChange,
  branchContact,
  onSave,
  isLoading = false,
}) => {
  const { t: tForms } = useFormsTranslation();

  // Get available branches for selection
  const { data: branchesData } = useLocalBranches();
  const availableBranches = Array.isArray(branchesData?.data)
    ? branchesData.data
    : [];

  // Use the updated useBranchContactForm hook following ContactPartnerDialog pattern
  const { form, handleSubmit, isEditing } = useBranchContactForm({
    branchContact,
    onSave,
    isOpen: open,
  });

  const handleCancel = () => {
    form.reset();
    onOpenChange(false);
  };

  const languageOptions = getLanguageOptions(tForms);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[800px] max-h-[90vh]'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <User className='h-5 w-5' />
            {isEditing
              ? tForms('branchContact.dialog.title.edit')
              : tForms('branchContact.dialog.title.create')}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? tForms('branchContact.dialog.description.edit')
              : tForms('branchContact.dialog.description.create')}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className='max-h-[calc(90vh-120px)] pr-4'>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className='space-y-6'
            >
              {/* Personal Information */}
              <Card>
                <CardHeader>
                  <CardTitle className='flex items-center gap-2 text-lg'>
                    <User className='h-4 w-4' />
                    {tForms('branchContact.dialog.sections.personalInfo')}
                  </CardTitle>
                </CardHeader>
                <CardContent className='space-y-4'>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    {/* Language */}
                    {/* Language */}
                    <FormField
                      control={form.control}
                      name='language'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {tForms('branchContact.dialog.labels.language')}
                          </FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                            disabled={isLoading}
                          >
                            <FormControl>
                              <SelectTrigger className='w-full min-h-[44px]'>
                                <SelectValue
                                  placeholder={tForms(
                                    'branchContact.dialog.placeholders.language'
                                  )}
                                />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {languageOptions.map(option => (
                                <SelectItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Branch Selection - Multiple Selection with Checkboxes */}
                  <FormField
                    control={form.control}
                    name='branchIds'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {tForms('branchContact.dialog.labels.branches')} *
                        </FormLabel>
                        <div className='text-xs text-muted-foreground mb-2'>
                          {tForms(
                            'branchContact.dialog.messages.selectAtLeastOneBranch'
                          )}
                        </div>
                        {availableBranches.length === 0 ? (
                          <div className='rounded-md border border-dashed p-8 text-center'>
                            <Building className='h-8 w-8 mx-auto mb-2 text-muted-foreground opacity-50' />
                            <p className='text-sm text-muted-foreground'>
                              {tForms(
                                'branchContact.dialog.messages.noBranchesAvailable'
                              )}
                            </p>
                          </div>
                        ) : (
                          <div className='rounded-md border'>
                            <ScrollArea className='h-[200px]'>
                              <div className='p-4 space-y-3'>
                                {availableBranches.map(branch => {
                                  const isChecked =
                                    field.value?.includes(branch.id) || false;
                                  return (
                                    <div
                                      key={branch.id}
                                      className='flex items-start space-x-3 p-3 rounded-md hover:bg-accent transition-colors'
                                    >
                                      <Checkbox
                                        id={`branch-${branch.id}`}
                                        checked={isChecked}
                                        disabled={isLoading}
                                        onCheckedChange={checked => {
                                          const currentValue =
                                            field.value || [];
                                          const newValue = checked
                                            ? [...currentValue, branch.id]
                                            : currentValue.filter(
                                                id => id !== branch.id
                                              );
                                          field.onChange(newValue);
                                        }}
                                      />
                                      <label
                                        htmlFor={`branch-${branch.id}`}
                                        className='flex-1 cursor-pointer'
                                      >
                                        <div className='flex items-center gap-2'>
                                          <Building className='h-4 w-4 text-muted-foreground' />
                                          <span className='font-medium text-sm'>
                                            {branch.name}
                                          </span>
                                        </div>
                                        <div className='text-xs text-muted-foreground mt-1'>
                                          {branch.street} {branch.number},{' '}
                                          {branch.zipCode} {branch.city}
                                        </div>
                                      </label>
                                    </div>
                                  );
                                })}
                              </div>
                            </ScrollArea>
                          </div>
                        )}
                        {field.value && field.value.length > 0 && (
                          <div className='mt-2 flex flex-wrap gap-2'>
                            <Badge variant='secondary' className='text-xs'>
                              {field.value.length}{' '}
                              {field.value.length === 1
                                ? tForms(
                                    'branchContact.dialog.labels.branchSelected'
                                  )
                                : tForms(
                                    'branchContact.dialog.labels.branchesSelected'
                                  )}
                            </Badge>
                          </div>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    {/* Function/Role */}
                    <FormField
                      control={form.control}
                      name='function'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {tForms('branchContact.dialog.labels.function')}
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder={tForms(
                                'branchContact.dialog.placeholders.function'
                              )}
                              className='w-full min-h-[44px]'
                              disabled={isLoading}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    {/* First Name */}
                    <FormField
                      control={form.control}
                      name='firstName'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {tForms('branchContact.dialog.labels.firstName')}
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder={tForms(
                                'branchContact.dialog.placeholders.firstName'
                              )}
                              className='w-full min-h-[44px]'
                              disabled={isLoading}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Last Name */}
                    <FormField
                      control={form.control}
                      name='lastName'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {tForms('branchContact.dialog.labels.lastName')}
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder={tForms(
                                'branchContact.dialog.placeholders.lastName'
                              )}
                              className='w-full min-h-[44px]'
                              disabled={isLoading}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Is Representative */}
                  <FormField
                    control={form.control}
                    name='isRepresentative'
                    render={({ field }) => (
                      <FormItem className='flex flex-row items-start space-x-3 space-y-0'>
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            disabled={isLoading}
                          />
                        </FormControl>
                        <div className='space-y-1 leading-none'>
                          <FormLabel>
                            {tForms(
                              'branchContact.dialog.labels.isRepresentative'
                            )}
                          </FormLabel>
                          <p className='text-sm text-muted-foreground'>
                            {tForms(
                              'branchContact.dialog.messages.representativeDescription'
                            )}
                          </p>
                        </div>
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* Contact Information */}
              <Card>
                <CardHeader>
                  <CardTitle className='flex items-center gap-2 text-lg'>
                    <Phone className='h-4 w-4' />
                    {tForms('branchContact.dialog.sections.contactInfo')}
                  </CardTitle>
                </CardHeader>
                <CardContent className='space-y-4'>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    {/* Email */}
                    <FormField
                      control={form.control}
                      name='email'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {tForms('branchContact.dialog.labels.email')}
                          </FormLabel>
                          <FormControl>
                            <Input
                              type='email'
                              placeholder={tForms(
                                'branchContact.dialog.placeholders.email'
                              )}
                              className='w-full min-h-[44px]'
                              disabled={isLoading}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Phone */}
                    <FormField
                      control={form.control}
                      name='phone'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {tForms('branchContact.dialog.labels.phone')}
                          </FormLabel>
                          <FormControl>
                            <Input
                              type='tel'
                              placeholder={tForms(
                                'branchContact.dialog.placeholders.phone'
                              )}
                              className='w-full min-h-[44px]'
                              disabled={isLoading}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Mobile */}
                  <FormField
                    control={form.control}
                    name='mobile'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {tForms('branchContact.dialog.labels.mobile')}
                        </FormLabel>
                        <FormControl>
                          <Input
                            type='tel'
                            placeholder={tForms(
                              'branchContact.dialog.placeholders.mobile'
                            )}
                            className='w-full min-h-[44px]'
                            disabled={isLoading}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Address */}
                  <FormField
                    control={form.control}
                    name='address'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {tForms('branchContact.dialog.labels.address')}
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder={tForms(
                              'branchContact.dialog.placeholders.address'
                            )}
                            disabled={isLoading}
                            rows={3}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <div className='flex justify-end gap-3 pt-4 border-t'>
                <Button
                  type='button'
                  variant='outline'
                  onClick={handleCancel}
                  disabled={isLoading}
                  className='flex items-center gap-2'
                >
                  <X className='h-4 w-4' />
                  {tForms('branchContact.dialog.actions.cancel')}
                </Button>
                <Button
                  type='submit'
                  disabled={isLoading}
                  className='flex items-center gap-2'
                >
                  <Save className='h-4 w-4' />
                  {isLoading
                    ? `${tForms('branchContact.dialog.actions.save')}...`
                    : tForms('branchContact.dialog.actions.save')}
                </Button>
              </div>
            </form>
          </Form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default BranchContactDialog;
